<template>
  <div>
    <div class="relative">
      <div class="flex flex-row items-center justify-start select-header">
        <span class="mr-2 text-sm font-medium text-primary">{{ name }}</span>
        <span
          class="flex items-center justify-center btn-open-label-pannel"
          @click="open = !open"
        >
          <FontAwesomeIcon
            :icon="faPlus"
            class="bg-primary text-white text-xs px-1.5 py-1 rounded cursor-pointer"
          />
        </span>
      </div>
      <SkyPopup
        :open="open"
        class="absolute top-full set-label-pannel"
      >
        <span
          v-if="!hasSelectableLabels"
          class="px-4 text-sm text-gray-500 whitespace-nowrap"
        >
          {{ $t('components.projects.labelsSelect.unavailable') }}
        </span>
        <template v-if="dedupeLabelsByName">
          <label
            v-for="group in labelGroupsByName"
            :key="group.dedupeKey"
            :for="`label-dedupe-${group.dedupeKey}`"
            class="flex flex-row items-center justify-start cursor-pointer select-none"
          >
            <input
              :id="`label-dedupe-${group.dedupeKey}`"
              type="checkbox"
              name="labels"
              class="hidden"
              :checked="isDedupeGroupChecked(group)"
              @change="onDedupeGroupChange(group, $event.target.checked)"
            />
            <div
              class="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded border border-gray-100 mr-2"
              :class="{
                'bg-primary text-white border-primary': isDedupeGroupChecked(group),
              }"
            >
              <FontAwesomeIcon
                :icon="faCheck"
                class="text-[10px] text-white"
              />
            </div>
            <LabelTag
              :label="group.representative"
              class="w-full text-xs"
            />
          </label>
        </template>
        <template v-else>
          <label
            v-for="label of typeLabels"
            :key="`label-${label._id}`"
            :for="`label-${label._id}`"
            class="flex flex-row items-center justify-start cursor-pointer select-none"
          >
            <input
              :id="`label-${label._id}`"
              v-model="internalLabelIds"
              type="checkbox"
              name="labels"
              :value="label._id"
              class="hidden"
            />
            <div
              class="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded border border-gray-100 mr-2"
              :class="{
                'bg-primary text-white border-primary': modelIds.includes(label._id),
              }"
            >
              <FontAwesomeIcon
                :icon="faCheck"
                class="text-[10px] text-white"
              />
            </div>
            <LabelTag
              :label="label"
              class="w-full text-xs"
            />
          </label>
        </template>
      </SkyPopup>
    </div>
    <span
      v-if="selectedLabels.length === 0"
      class="mt-3 text-xs text-gray-400"
    >
      {{ $t('components.projects.labelsSelect.empty') }}
    </span>
    <div
      v-else
      class="flex flex-row flex-wrap items-start justify-start gap-2 mt-3"
    >
      <LabelTag
        v-for="label in selectedLabels"
        :key="label._id"
        :label="label"
        class="text-xs"
      />
    </div>
  </div>
</template>

<script setup>
import { faCheck, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { difference, map } from 'lodash';
import filter from 'lodash/filter';
import uniq from 'lodash/uniq';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import LabelTag from '@/modules/projects/components/LabelTag.vue';
import { labelMergeGroupKey } from '@/modules/projects/projects.functions.js';
import { dependenciesType } from '../projects.constants.js';

const emit = defineEmits(['update:modelValue']);
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  name: {
    type: String,
    default: 'Catégories',
  },
  type: {
    type: String,
    default: 'category',
  },
  multi: {
    type: Boolean,
    default: true,
  },
  useDependencies: {
    type: Boolean,
    default: false,
  },
  /**
   * Admin multi-apps : les dépendances catégorie → thème → tag pointent vers des `_id` par app ;
   * avec des labels fusionnés, `getLabelsValidDependencies` supprimait thèmes/étiquettes incohérents
   * et la liste filtrée ne recevait pas les bons `labelIds`.
   */
  skipDependencyValidation: {
    type: Boolean,
    default: false,
  },
  /**
   * Regroupe les labels du même type par nom exact (trim, sensible à la casse) ; la sélection envoie tous
   * les `_id` dont le libellé coincide (ex. même chaîne sur plusieurs apps).
   */
  dedupeLabelsByName: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const open = ref(false);
const labels = computed(() => store.getters['projects/getLabels'] || []);
/** Toujours un tableau — évite `.includes` / `[...]` sur `undefined` (ex. tâches admin sans `labelIds`). */
const modelIds = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []));
const needDependencies = computed(() => Object.keys(dependenciesType).includes(props.type));
const dependenciesLabels = computed(() =>
  filter(labels.value, (label) => needDependencies.value && label.type === dependenciesType[props.type] && !label.disabledAt && modelIds.value.includes(label._id)),
);
const typeLabels = computed(() =>
  filter(labels.value, (label) => {
    if (label.type !== props.type || label.disabledAt) {
      return false;
    }
    if (props.skipDependencyValidation) {
      return true;
    }
    const labelDependencies = label.dependencies || [];
    const hasDependenciesMatch = labelDependencies.length === 0 || dependenciesLabels.value.some((l) => labelDependencies.includes(l._id));
    const isDependency = !props.useDependencies || (props.useDependencies && hasDependenciesMatch);
    return isDependency;
  }),
);

/** Une entrée par nom distinct (casse comprise) ; `ids` = tous les `_id` à inclure dans le filtre API. */
const labelGroupsByName = computed(() => {
  const raw = typeLabels.value;
  if (!props.dedupeLabelsByName || raw.length === 0) {
    return [];
  }
  const groupsMap = new Map();
  raw.forEach((label) => {
    const key = labelMergeGroupKey(label.name);
    if (!groupsMap.has(key)) {
      groupsMap.set(key, { ids: [label._id], representative: label, dedupeKey: `${props.type}-${key}` });
      return;
    }
    groupsMap.get(key).ids.push(label._id);
  });
  return [...groupsMap.values()];
});

const hasSelectableLabels = computed(() => (props.dedupeLabelsByName ? labelGroupsByName.value.length > 0 : typeLabels.value.length > 0));

const selectedLabels = computed(() => {
  const selected = filter(labels.value, (label) => label.type === props.type && props.modelValue.includes(label?._id));
  if (!props.dedupeLabelsByName) {
    return selected;
  }
  const seen = new Map();
  selected.forEach((label) => {
    const nk = labelMergeGroupKey(label.name);
    if (!seen.has(nk)) {
      seen.set(nk, label);
    }
  });
  return [...seen.values()];
});

function allIdsOfTypeInStore() {
  return filter(labels.value, (l) => l.type === props.type).map((l) => l._id);
}

function isDedupeGroupChecked(group) {
  return group.ids.some((id) => modelIds.value.includes(id));
}

function onDedupeGroupChange(group, checked) {
  const { ids } = group;
  const mv = modelIds.value;
  if (props.multi) {
    if (checked) {
      emit('update:modelValue', uniq([...mv, ...ids]));
    } else {
      emit(
        'update:modelValue',
        mv.filter((id) => !ids.includes(id)),
      );
    }
    return;
  }
  const typeIds = new Set(allIdsOfTypeInStore());
  const rest = mv.filter((id) => !typeIds.has(id));
  if (checked) {
    emit('update:modelValue', [...rest, ...ids]);
  } else {
    emit('update:modelValue', rest);
  }
}

function getLabelsValidDependencies(value) {
  const currentValueLabels = filter(labels.value, (label) => value.includes(label?._id));
  const validValues = currentValueLabels.filter((label) => {
    const labelDependencies = label.dependencies || [];
    if (labelDependencies.length === 0) return true;
    const dependenciesValid = labelDependencies.some((labelId) => value.includes(labelId));
    if (dependenciesValid) return true;
    return false;
  });
  return map(validValues, (label) => label._id);
}

const internalLabelIds = computed({
  get() {
    return modelIds.value;
  },
  set(value) {
    if (props.skipDependencyValidation) {
      if (props.multi) {
        emit('update:modelValue', value);
        return;
      }
      const selectedLabelsIds = selectedLabels.value.map((label) => label._id);
      emit('update:modelValue', difference(value, selectedLabelsIds));
      return;
    }
    if (props.multi) {
      emit('update:modelValue', getLabelsValidDependencies(value));
      return;
    }
    const selectedLabelsIds = selectedLabels.value.map((label) => label._id);
    const uniqLabelsCategory = difference(value, selectedLabelsIds);
    emit('update:modelValue', getLabelsValidDependencies(uniqLabelsCategory));
  },
});
function handleClickOutside(event) {
  if (open.value === false) {
    return;
  }

  const { target } = event;
  const isOutsideClick = !target.closest('.set-label-pannel') && !target.closest('.btn-open-label-pannel');

  if (!isOutsideClick) {
    return;
  }

  open.value = false;
}

onMounted(() => {
  document.querySelector('body').addEventListener('click', handleClickOutside, { passive: true });
});

onUnmounted(() => {
  document.querySelector('body').removeEventListener('click', handleClickOutside);
});
</script>
